import { ChevronDown } from "lucide-react";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { plainText } from "../lib/richtext";
import { asFaqs } from "../lib/section-data";
import { RichText } from "../components/RichText";
import { RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

/**
 * FAQs as a set of native `<details>` elements.
 *
 * Native disclosure gives keyboard operation, screen-reader semantics and
 * in-page find for free. A custom accordion would have to reimplement all
 * three, usually incompletely.
 *
 * With `allowMultipleOpen` off, the panels share a `name`, which browsers treat
 * as an exclusive accordion group. Where that is unsupported they simply all
 * stay open — a graceful degradation rather than a broken control.
 */
export default function FaqAccordionBlock({
  content,
  settings,
  data,
}: SectionProps<BlockContentMap["faqAccordion"]>) {
  const onDark = isDarkBackground(settings);

  const faqs = (asFaqs(data) ?? [])
    .filter((faq) => (content.faqIds.length > 0 ? content.faqIds.includes(faq.id) : true))
    .filter((faq) => (content.categoryLabel ? faq.categoryLabel === content.categoryLabel : true));

  // A questions heading above nothing reads as a fault, so an empty result
  // removes the section rather than leaving an orphaned heading.
  if (faqs.length === 0) return null;

  return (
    <SectionShell settings={settings}>
      <SectionHeading
        eyebrow={content.eyebrow}
        heading={content.heading}
        intro={content.intro}
        settings={settings}
      />

      {/* Alignment applies to the heading; the questions themselves always read
          left-aligned, because centred body copy in a disclosure is hard work. */}
      <RevealList as="div" className="space-y-3 text-left">
        {faqs.map((faq) => (
          <RevealItem as="div" key={faq.id}>
          <details
            name={content.allowMultipleOpen ? undefined : `faq-${settings.anchorId || "group"}`}
            className={cn(
              "group rounded-lg px-6 py-5",
              onDark ? "bg-navy-800" : "bg-white shadow-sm",
              // A disclosure genuinely is clickable, so this one may be more
              // assertive than the passive cards — the pointer is over a
              // control, and the feedback should say so.
              //
              // Both properties are named in the transition. `transition-shadow`
              // alone would have left the colour snapping while the shadow
              // eased, which reads as two effects rather than one.
              "transition-[box-shadow,background-color] duration-200 motion-reduce:transition-none",
              // Focus matches hover throughout: the summary is a real tab stop,
              // and a keyboard user arriving at a question should see the same
              // panel highlighted that a mouse user does.
              onDark
                ? "hover:bg-navy-600 focus-within:bg-navy-600"
                : "hover:bg-navy-100 hover:shadow-md focus-within:bg-navy-100 focus-within:shadow-md",
            )}
          >
            <summary
              className={cn(
                "flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg marker:content-['']",
                onDark ? "text-cream-50" : "text-navy-900",
              )}
            >
              {faq.question}
              <ChevronDown
                aria-hidden
                className="size-5 shrink-0 text-gold-600 transition-transform group-open:rotate-180"
              />
            </summary>
            <div
              className={cn(
                "mt-4 border-t pt-4",
                onDark ? "border-navy-600 text-navy-100" : "border-ink-100",
              )}
            >
              <RichText doc={faq.answer} />
            </div>
          </details>
          </RevealItem>
        ))}
      </RevealList>

      {/**
       * FAQPage structured data, which is what turns a plain blue search result
       * into one that lists the questions this page answers. Built from the
       * same array the accordion renders, so the two cannot drift — Google
       * treats markup that does not match visible content as a violation.
       */}
      {content.emitStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: { "@type": "Answer", text: plainText(faq.answer) },
              })),
            }).replace(/</g, "\\u003c"),
          }}
        />
      )}
    </SectionShell>
  );
}
